import type { ButtonHTMLAttributes } from "react";
import { cn } from "../lib/utils.js";

type Variant = "primary" | "secondary" | "danger" | "ghost" | "accent";
type Size = "sm" | "md" | "lg";

export function Button({
  variant = "primary",
  size = "md",
  className,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: Variant;
  size?: Size;
}): JSX.Element {
  const base =
    "inline-flex items-center justify-center font-medium transition-all duration-150 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 disabled:opacity-50 disabled:cursor-not-allowed select-none active:scale-[0.98] disabled:active:scale-100";

  const sizes: Record<Size, string> = {
    sm: "px-2 py-1 text-xs rounded shadow-sm",
    md: "px-2.5 py-1.5 text-xs rounded-md shadow-sm",
    lg: "px-3.5 py-2 text-sm rounded-md shadow-sm",
  };

  const variants: Record<Variant, string> = {
    primary:
      "bg-gradient-to-b from-simuflex-navy to-simuflex-deepNavy text-white hover:from-simuflex-deepNavy hover:to-simuflex-deepNavy border border-simuflex-deepNavy/25 shadow-sm hover:shadow focus-visible:outline-simuflex-navy",
    accent:
      "bg-gradient-to-b from-simuflex-green to-[#16a34a] text-white hover:from-[#16a34a] hover:to-[#16a34a] border border-[#15803d] shadow-sm hover:shadow focus-visible:outline-simuflex-green",
    secondary:
      "bg-gradient-to-b from-white to-slate-50 text-slate-700 border border-slate-200/80 hover:from-slate-50 hover:to-slate-100 hover:text-slate-900 focus-visible:outline-slate-500",
    danger:
      "bg-gradient-to-b from-simuflex-orange to-[#e05b26] text-white hover:from-[#e05b26] hover:to-[#e05b26] border border-[#c44917] shadow-sm focus-visible:outline-simuflex-orange",
    ghost:
      "text-slate-600 hover:bg-slate-50 hover:text-slate-900 shadow-none border border-transparent",
  };

  return <button className={cn(base, sizes[size], variants[variant], className)} {...props} />;
}
